/**
 * CORPORATE ORG-STRUCTURE endpoints (Policy / CostCenter / Department / Title) —
 * migration guide Bölüm 3.9. See api/types/corporate.ts for field-shape notes.
 *
 * CostCenter and Policy (+ its 4 exception sub-types) routes are confirmed against the
 * real API: request/response shapes cross-checked with the live swagger document
 * (`/swagger/docs/v1` on the API host) AND with curl (`CostCenter/GetCostCenters`,
 * `Policy/GetPolicies` return real data with a live test session; the by-id and
 * exception-list routes 200 with a business-logic `hasError` for this test account's
 * data, which confirms the route + request shape are accepted).
 *
 * CorporateDepartment/CorporateTitle routes were absent from the 2026-08-27 swagger scan
 * but are CONFIRMED live 2026-08-31 against `localhost:1538` — full CRUD works end to end
 * (real Add/Get/Edit/Delete/UpdateParent round-trips), response field `Departments`/`Titles`.
 */
import { apiPost, apiPostSession } from '../client';
import type { ResponseBase } from '../types/common';
import type {
  CBExceptionListResponse,
  CorporateHotelReservationException,
  CorporatePolicy,
  CorporatePolicyCBException,
  CorporatePolicyFTException,
  CorporatePolicyHBException,
  CostCenter,
  CostCenterListResponse,
  Department,
  DepartmentListResponse,
  DepartmentTreeResponse,
  FTExceptionListResponse,
  HBExceptionListResponse,
  HotelReservationExceptionListResponse,
  PolicyListResponse,
  Title,
  TitleListResponse,
  TitleTreeResponse,
  UpdateDepartmentParentRequest,
  UpdateTitleParentRequest,
} from '../types/corporate';

/* ----------------------------- Cost Center ----------------------------- */

export function getCostCenters(): Promise<CostCenterListResponse> {
  return apiPostSession<CostCenterListResponse>('CostCenter/GetCostCenters');
}

export function getCostCenterById(costCenterID: number): Promise<CostCenterListResponse> {
  return apiPost<{ costCenter: Partial<CostCenter> }, CostCenterListResponse>(
    'CostCenter/GetCostCenterById',
    { costCenter: { costCenterID } }
  );
}

export function addCostCenter(costCenter: Omit<CostCenter, 'costCenterID'>): Promise<ResponseBase> {
  return apiPost<{ costCenter: Omit<CostCenter, 'costCenterID'> }, ResponseBase>(
    'CostCenter/AddCostCenter',
    { costCenter }
  );
}

export function editCostCenter(costCenter: CostCenter): Promise<ResponseBase> {
  return apiPost<{ costCenter: CostCenter }, ResponseBase>('CostCenter/EditCostCenter', {
    costCenter,
  });
}

export function deleteCostCenter(costCenterID: number): Promise<ResponseBase> {
  return apiPost<{ costCenter: Partial<CostCenter> }, ResponseBase>('CostCenter/DeleteCostCenter', {
    costCenter: { costCenterID },
  });
}

/* -------------------------------- Policy -------------------------------- */

export function getPolicies(): Promise<PolicyListResponse> {
  return apiPostSession<PolicyListResponse>('Policy/GetPolicies');
}

export function getPolicyById(policyID: number): Promise<PolicyListResponse> {
  return apiPost<{ corporatePolicy: Partial<CorporatePolicy> }, PolicyListResponse>(
    'Policy/GetPolicyById',
    { corporatePolicy: { policyID } }
  );
}

export function addPolicy(corporatePolicy: Omit<CorporatePolicy, 'policyID'>): Promise<PolicyListResponse> {
  return apiPost<{ corporatePolicy: Omit<CorporatePolicy, 'policyID'> }, PolicyListResponse>(
    'Policy/AddPolicy',
    { corporatePolicy }
  );
}

export function editPolicy(corporatePolicy: CorporatePolicy): Promise<ResponseBase> {
  return apiPost<{ corporatePolicy: CorporatePolicy }, ResponseBase>('Policy/EditPolicy', {
    corporatePolicy,
  });
}

export function deletePolicy(policyID: number): Promise<ResponseBase> {
  return apiPost<{ corporatePolicy: Partial<CorporatePolicy> }, ResponseBase>('Policy/DeletePolicy', {
    corporatePolicy: { policyID },
  });
}

/* ------------------------- Policy exceptions ------------------------- */

export function getPolicyCBExceptions(policyID: number): Promise<CBExceptionListResponse> {
  return apiPost<{ corporateFlightExceptionsCB: Partial<CorporatePolicyCBException> }, CBExceptionListResponse>(
    'Policy/GetPolicyCBExceptions',
    { corporateFlightExceptionsCB: { policyID } }
  );
}

export function getPolicyCBExceptionById(flightException2ID: number): Promise<CBExceptionListResponse> {
  return apiPost<{ corporateFlightExceptionsCB: Partial<CorporatePolicyCBException> }, CBExceptionListResponse>(
    'Policy/GetPolicyCBExceptionById',
    { corporateFlightExceptionsCB: { flightException2ID } }
  );
}

export function addPolicyCBException(
  corporateFlightExceptionsCB: Omit<CorporatePolicyCBException, 'flightException2ID'>
): Promise<ResponseBase> {
  return apiPost<
    { corporateFlightExceptionsCB: Omit<CorporatePolicyCBException, 'flightException2ID'> },
    ResponseBase
  >('Policy/AddPolicyCBException', { corporateFlightExceptionsCB });
}

export function editPolicyCBException(
  corporateFlightExceptionsCB: CorporatePolicyCBException
): Promise<ResponseBase> {
  return apiPost<{ corporateFlightExceptionsCB: CorporatePolicyCBException }, ResponseBase>(
    'Policy/EditPolicyCBException',
    { corporateFlightExceptionsCB }
  );
}

export function deletePolicyCBException(flightException2ID: number): Promise<ResponseBase> {
  return apiPost<{ corporateFlightExceptionsCB: Partial<CorporatePolicyCBException> }, ResponseBase>(
    'Policy/DeletePolicyCBException',
    { corporateFlightExceptionsCB: { flightException2ID } }
  );
}

export function getPolicyFTExceptions(policyID: number): Promise<FTExceptionListResponse> {
  return apiPost<{ corporateFlightExceptionsFTB: Partial<CorporatePolicyFTException> }, FTExceptionListResponse>(
    'Policy/GetPolicyFTExceptions',
    { corporateFlightExceptionsFTB: { policyID } }
  );
}

export function getPolicyFTExceptionById(flightException3ID: number): Promise<FTExceptionListResponse> {
  return apiPost<{ corporateFlightExceptionsFTB: Partial<CorporatePolicyFTException> }, FTExceptionListResponse>(
    'Policy/GetPolicyFTExceptionById',
    { corporateFlightExceptionsFTB: { flightException3ID } }
  );
}

export function addPolicyFTException(
  corporateFlightExceptionsFTB: Omit<CorporatePolicyFTException, 'flightException3ID'>
): Promise<ResponseBase> {
  return apiPost<
    { corporateFlightExceptionsFTB: Omit<CorporatePolicyFTException, 'flightException3ID'> },
    ResponseBase
  >('Policy/AddPolicyFTException', { corporateFlightExceptionsFTB });
}

export function editPolicyFTException(
  corporateFlightExceptionsFTB: CorporatePolicyFTException
): Promise<ResponseBase> {
  return apiPost<{ corporateFlightExceptionsFTB: CorporatePolicyFTException }, ResponseBase>(
    'Policy/EditPolicyFTException',
    { corporateFlightExceptionsFTB }
  );
}

export function deletePolicyFTException(flightException3ID: number): Promise<ResponseBase> {
  return apiPost<{ corporateFlightExceptionsFTB: Partial<CorporatePolicyFTException> }, ResponseBase>(
    'Policy/DeletePolicyFTException',
    { corporateFlightExceptionsFTB: { flightException3ID } }
  );
}

export function getPolicyHBExceptions(policyID: number): Promise<HBExceptionListResponse> {
  return apiPost<{ corporateFlightExceptionsHB: Partial<CorporatePolicyHBException> }, HBExceptionListResponse>(
    'Policy/GetPolicyHBExceptions',
    { corporateFlightExceptionsHB: { policyID } }
  );
}

export function getPolicyHBExceptionById(flightExceptionID: number): Promise<HBExceptionListResponse> {
  return apiPost<{ corporateFlightExceptionsHB: Partial<CorporatePolicyHBException> }, HBExceptionListResponse>(
    'Policy/GetPolicyHBExceptionById',
    { corporateFlightExceptionsHB: { flightExceptionID } }
  );
}

export function addPolicyHBException(
  corporateFlightExceptionsHB: Omit<CorporatePolicyHBException, 'flightExceptionID'>
): Promise<ResponseBase> {
  return apiPost<
    { corporateFlightExceptionsHB: Omit<CorporatePolicyHBException, 'flightExceptionID'> },
    ResponseBase
  >('Policy/AddPolicyHBException', { corporateFlightExceptionsHB });
}

export function editPolicyHBException(
  corporateFlightExceptionsHB: CorporatePolicyHBException
): Promise<ResponseBase> {
  return apiPost<{ corporateFlightExceptionsHB: CorporatePolicyHBException }, ResponseBase>(
    'Policy/EditPolicyHBException',
    { corporateFlightExceptionsHB }
  );
}

export function deletePolicyHBException(flightExceptionID: number): Promise<ResponseBase> {
  return apiPost<{ corporateFlightExceptionsHB: Partial<CorporatePolicyHBException> }, ResponseBase>(
    'Policy/DeletePolicyHBException',
    { corporateFlightExceptionsHB: { flightExceptionID } }
  );
}

export function getHotelReservationExceptions(
  policyID: number
): Promise<HotelReservationExceptionListResponse> {
  return apiPost<
    { corporateHotelReservationException: Partial<CorporateHotelReservationException> },
    HotelReservationExceptionListResponse
  >('Policy/GetHotelReservationExceptions', { corporateHotelReservationException: { policyID } });
}

export function getHotelReservationExceptionById(
  hotelReservationExceptionID: number
): Promise<HotelReservationExceptionListResponse> {
  return apiPost<
    { corporateHotelReservationException: Partial<CorporateHotelReservationException> },
    HotelReservationExceptionListResponse
  >('Policy/GetHotelReservationExceptionById', {
    corporateHotelReservationException: { hotelReservationExceptionID },
  });
}

export function addHotelReservationException(
  corporateHotelReservationException: Omit<CorporateHotelReservationException, 'hotelReservationExceptionID'>
): Promise<ResponseBase> {
  return apiPost<
    {
      corporateHotelReservationException: Omit<
        CorporateHotelReservationException,
        'hotelReservationExceptionID'
      >;
    },
    ResponseBase
  >('Policy/AddHotelReservationException', { corporateHotelReservationException });
}

export function editHotelReservationException(
  corporateHotelReservationException: CorporateHotelReservationException
): Promise<ResponseBase> {
  return apiPost<{ corporateHotelReservationException: CorporateHotelReservationException }, ResponseBase>(
    'Policy/EditHotelReservationException',
    { corporateHotelReservationException }
  );
}

export function deleteHotelReservationException(hotelReservationExceptionID: number): Promise<ResponseBase> {
  return apiPost<
    { corporateHotelReservationException: Partial<CorporateHotelReservationException> },
    ResponseBase
  >('Policy/DeleteHotelReservationException', {
    corporateHotelReservationException: { hotelReservationExceptionID },
  });
}

/* ------------------------------ Department ------------------------------
 * CONFIRMED live 2026-08-31 (`localhost:1538`, session Arkgsa/MASTER): GetDepartments /
 * GetDepartmentById / AddDepartment / EditDepartment / DeleteDepartment / UpdateParent all
 * work; response field is `Departments` (→ `departments`), even for the by-id lookup.
 * `CorporateDepartment/GetTree` (bare session body, response `{ Tree: DepartmentTreeNode[] }`)
 * is wired below and used as the primary source for the org-chart / indented list, with the
 * client-side `parentDepartmentId` build kept as the fallback.
 */

export function getDepartments(): Promise<DepartmentListResponse> {
  return apiPostSession<DepartmentListResponse>('CorporateDepartment/GetDepartments');
}

/** `CorporateDepartment/GetDepartmentById` — response field is `departments` (an array even
 *  for one row). Used to refetch the fresh row when the edit dialog opens. */
export function getDepartmentById(id: number): Promise<DepartmentListResponse> {
  return apiPost<{ department: Partial<Department> }, DepartmentListResponse>(
    'CorporateDepartment/GetDepartmentById',
    { department: { id } }
  );
}

export function getDepartmentTree(): Promise<DepartmentTreeResponse> {
  return apiPostSession<DepartmentTreeResponse>('CorporateDepartment/GetTree');
}

export function addDepartment(department: Omit<Department, 'id'>): Promise<ResponseBase> {
  return apiPost<{ department: Omit<Department, 'id'> }, ResponseBase>('CorporateDepartment/AddDepartment', {
    department,
  });
}

export function editDepartment(department: Department): Promise<ResponseBase> {
  return apiPost<{ department: Department }, ResponseBase>('CorporateDepartment/EditDepartment', {
    department,
  });
}

export function deleteDepartment(id: number): Promise<ResponseBase> {
  return apiPost<{ department: Partial<Department> }, ResponseBase>('CorporateDepartment/DeleteDepartment', {
    department: { id },
  });
}

export function updateDepartmentParent(body: UpdateDepartmentParentRequest): Promise<ResponseBase> {
  return apiPost<UpdateDepartmentParentRequest, ResponseBase>('CorporateDepartment/UpdateParent', body);
}

/* -------------------------------- Title --------------------------------
 * CONFIRMED live 2026-08-31 — see Department note above (same shape/conventions).
 */

export function getTitles(): Promise<TitleListResponse> {
  return apiPostSession<TitleListResponse>('CorporateTitle/GetTitles');
}

/** `CorporateTitle/GetTitleById` — response field is `titles`. Refetch-on-edit-open. */
export function getTitleById(id: number): Promise<TitleListResponse> {
  return apiPost<{ title: Partial<Title> }, TitleListResponse>('CorporateTitle/GetTitleById', {
    title: { id },
  });
}

export function getTitleTree(): Promise<TitleTreeResponse> {
  return apiPostSession<TitleTreeResponse>('CorporateTitle/GetTree');
}

export function addTitle(title: Omit<Title, 'id'>): Promise<ResponseBase> {
  return apiPost<{ title: Omit<Title, 'id'> }, ResponseBase>('CorporateTitle/AddTitle', { title });
}

export function editTitle(title: Title): Promise<ResponseBase> {
  return apiPost<{ title: Title }, ResponseBase>('CorporateTitle/EditTitle', { title });
}

export function deleteTitle(id: number): Promise<ResponseBase> {
  return apiPost<{ title: Partial<Title> }, ResponseBase>('CorporateTitle/DeleteTitle', { title: { id } });
}

export function updateTitleParent(body: UpdateTitleParentRequest): Promise<ResponseBase> {
  return apiPost<UpdateTitleParentRequest, ResponseBase>('CorporateTitle/UpdateParent', body);
}
